// Quiz Router - Main Navigation and Step Management System
(function() {
  'use strict';

  class LumaQuizRouter {
    constructor() {
      this.currentStep = 1;
      this.stepHistory = [];
      this.quizState = window.quizState;
      this.isLoading = false;
      
      // Step flow mapping
      this.stepFlows = {
        1: this.getStep1NextStep.bind(this),
        2: this.getStep2NextStep.bind(this),
        '2.1': () => null, // Coming soon
        '2.2': () => 3.2,
        '3.1': this.getStep31NextStep.bind(this),
        '3.2': () => null, // Coming soon
        4: this.getStep4NextStep.bind(this),
        5: () => 6,
        6: () => 7,
        7: () => 8,
        8: () => 9,
        9: () => null // Quiz complete
      };

      this.init();
    }

    async init() {
      console.log('🚀 Quiz Router initializing...');
      
      // Set up global event listeners
      this.setupGlobalEvents();
      
      // Determine current step from URL or default to 1
      this.currentStep = this.getStepFromURL() || 1;
      
      // Load the current step
      await this.loadStep(this.currentStep);
      
      console.log('✅ Quiz Router ready');
    }

    setupGlobalEvents() {
      // Handle all clicks globally
      document.addEventListener('click', this.handleGlobalClick.bind(this));
      
      // Handle browser back/forward
      window.addEventListener('popstate', this.handlePopState.bind(this));
      
      // Handle form submissions
      document.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    handleGlobalClick(e) {
      // Prevent multiple clicks during loading
      if (this.isLoading) {
        e.preventDefault();
        return;
      }

      // Handle back buttons
      if (e.target.closest('.back-button')) {
        e.preventDefault();
        this.goToPreviousStep();
        return;
      }

      // Handle next buttons
      if (e.target.closest('.next-button')) {
        e.preventDefault();
        this.goToNextStep();
        return;
      }

      // Handle option buttons
      if (e.target.closest('.option-button')) {
        e.preventDefault();
        this.handleOptionClick(e.target.closest('.option-button'));
        return;
      }

      // Delegate to current step module
      const currentStepModule = this.getCurrentStepModule();
      if (currentStepModule && typeof currentStepModule.handleClick === 'function') {
        currentStepModule.handleClick(e);
      }
    }

    handleOptionClick(button) {
      const option = button.dataset.option;
      const question = button.dataset.question;
      
      if (option && question) {
        // Save the answer
        this.quizState.set(question, option);
        
        // Let the current step handle the visual feedback
        const currentStepModule = this.getCurrentStepModule();
        if (currentStepModule && typeof currentStepModule.handleClick === 'function') {
          const fakeEvent = { target: button };
          currentStepModule.handleClick(fakeEvent);
        }
      }
    }

    handleFormSubmit(e) {
      e.preventDefault();
      
      const currentStepModule = this.getCurrentStepModule();
      if (currentStepModule && typeof currentStepModule.handleSubmit === 'function') {
        currentStepModule.handleSubmit(e);
      }
    }

    handlePopState(e) {
      const step = this.getStepFromURL() || 1;
      if (step !== this.currentStep) {
        this.loadStep(step, true);
      }
    }

    getStepFromURL() {
      const path = window.location.pathname;
      const match = path.match(/\/quiz\/step-(\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : null;
    }

    getCurrentStepModule() {
      const stepKey = `step_${this.currentStep.toString().replace('.', '_')}`;
      return window[`QuizStep_${stepKey}`];
    }

    async loadStep(stepNumber, isBack = false) {
      if (this.isLoading) return;
      
      this.isLoading = true;
      this.showLoading();

      try {
        console.log(`📍 Loading step ${stepNumber}...`);

        // Clean up current step
        await this.cleanupCurrentStep();

        // Load step module if not already loaded
        await this.loadStepModule(stepNumber);

        // Initialize the step
        const stepModule = this.getCurrentStepModuleByNumber(stepNumber);
        if (stepModule && typeof stepModule.init === 'function') {
          await stepModule.init(this.quizState);
        }

        // Update current step
        this.currentStep = stepNumber;
        
        // Update URL without triggering navigation
        this.updateURL(stepNumber);
        
        // Update browser history
        if (!isBack) {
          this.stepHistory.push(stepNumber);
        }

        console.log(`✅ Step ${stepNumber} loaded`);

      } catch (error) {
        console.error(`❌ Error loading step ${stepNumber}:`, error);
        this.showError(`Failed to load step ${stepNumber}`);
      } finally {
        this.hideLoading();
        this.isLoading = false;
      }
    }

    async loadStepModule(stepNumber) {
      const stepKey = `step_${stepNumber.toString().replace('.', '_')}`;
      const moduleKey = `QuizStep_${stepKey}`;
      
      // Check if module is already loaded
      if (window[moduleKey]) {
        return;
      }

      // Check if we have an asset URL for this step
      const assetKey = stepKey.replace('_', '-');
      if (window.QUIZ_ASSETS && window.QUIZ_ASSETS[assetKey]) {
        try {
          await this.loadScript(window.QUIZ_ASSETS[assetKey]);
        } catch (error) {
          console.error(`Failed to load step module: ${assetKey}`, error);
          throw error;
        }
      } else {
        throw new Error(`No module found for step ${stepNumber}`);
      }
    }

    loadScript(url) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    getCurrentStepModuleByNumber(stepNumber) {
      const stepKey = `step_${stepNumber.toString().replace('.', '_')}`;
      return window[`QuizStep_${stepKey}`];
    }

    async cleanupCurrentStep() {
      const currentStepModule = this.getCurrentStepModule();
      if (currentStepModule && typeof currentStepModule.destroy === 'function') {
        await currentStepModule.destroy();
      }
    }

    updateURL(stepNumber) {
      const url = `/quiz/step-${stepNumber}`;
      if (window.location.pathname !== url) {
        window.history.pushState({ step: stepNumber }, '', url);
      }
    }

    async goToNextStep() {
      const nextStep = this.getNextStepNumber();
      
      if (nextStep === null) {
        this.handleQuizCompletion();
        return;
      }

      await this.loadStep(nextStep);
    }

    async goToPreviousStep() {
      if (this.stepHistory.length === 0) return;
      
      const previousStep = this.stepHistory.pop();
      await this.loadStep(previousStep, true);
    }

    getNextStepNumber() {
      const flowFunction = this.stepFlows[this.currentStep];
      if (typeof flowFunction === 'function') {
        return flowFunction();
      }
      return null;
    }

    // Step-specific next step logic
    getStep1NextStep() {
      const guardianFor = this.quizState.get('guardian_for');
      if (guardianFor === 'someone_else') return 2;
      if (guardianFor === 'myself') return 2.1;
      if (guardianFor === 'both') return 2.2;
      return 3;
    }

    getStep2NextStep() {
      const numberOfPeople = this.quizState.get('number_of_people');
      return numberOfPeople === '1' ? 3.1 : 3.2;
    }

    getStep31NextStep() {
      const relationship = this.quizState.get('relationship');
      return relationship === 'romantic_partner' ? 4 : null;
    }

    getStep4NextStep() {
      const partnerGender = this.quizState.get('partner_gender');
      return partnerGender === 'female' ? 5 : null;
    }

    handleQuizCompletion() {
      console.log('🎉 Quiz completed!');
      console.log('Final data:', this.quizState.getAll());
      
      // Show completion message or redirect
      alert('Quiz completed! Thank you for your responses.');
      
      // Optionally redirect to a thank you page
      // window.location.href = '/quiz/complete';
    }

    showLoading() {
      const loading = document.getElementById('quiz-loading');
      if (loading) {
        loading.style.display = 'block';
      }
    }

    hideLoading() {
      const loading = document.getElementById('quiz-loading');
      if (loading) {
        loading.style.display = 'none';
      }
    }

    showError(message) {
      // Simple error display - you can enhance this
      console.error(message);
      alert(`Error: ${message}`);
    }

    // Public API methods
    getCurrentStep() {
      return this.currentStep;
    }

    getQuizData() {
      return this.quizState.getAll();
    }

    updateQuizData(key, value) {
      this.quizState.set(key, value);
    }

    resetQuiz() {
      this.quizState.clear();
      this.stepHistory = [];
      this.loadStep(1);
    }
  }

  // Initialize router when DOM is ready
  function initRouter() {
    if (window.lumaQuizRouter) {
      console.log('Router already initialized');
      return;
    }

    window.lumaQuizRouter = new LumaQuizRouter();
  }

  // Initialize immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRouter);
  } else {
    initRouter();
  }

  console.log('📦 Quiz Router module loaded');
})();